import { combinePortfolio, runBacktest } from "./engine";
import { computeMetrics } from "./metrics";
import { calculateKaufmanEfficiencyRatio } from "../Chart/indicatorCalculations";
import { detectHMMRegimesCached } from "../Chart/hmmRegimes";

/** Independent in-sample and out-of-sample runs, each starting flat with the same capital.
 * The first test-bar signal is ignored; only signals formed inside the test window can trade. */
export function splitBacktest(candles, desired, settings, trainPct = 70) {
  if (!Array.isArray(candles) || !Array.isArray(desired) || candles.length !== desired.length || candles.length < 4) {
    throw new Error("A split backtest needs at least four candles and one signal per candle.");
  }
  const percent = Math.min(90, Math.max(50, Number(trainPct) || 70));
  const splitIndex = Math.min(candles.length - 2, Math.max(2, Math.floor((candles.length * percent) / 100)));
  const trainCandles = candles.slice(0, splitIndex);
  const trainDesired = desired.slice(0, splitIndex);
  const testCandles = candles.slice(splitIndex);
  const testDesired = desired.slice(splitIndex);
  testDesired[0] = 0;

  const trainRun = runBacktest(trainCandles, trainDesired, settings);
  const testRun = runBacktest(testCandles, testDesired, settings);
  const capital = Number(settings?.capital) || 10000;
  return {
    splitIndex,
    splitTime: testCandles[0].time,
    trainRun,
    testRun,
    trainMetrics: computeMetrics(trainRun, capital),
    testMetrics: computeMetrics(testRun, capital),
  };
}

/** Equal-weight portfolio validation using the same strategy set and parameters in both periods. */
export function splitPortfolioBacktest(candles, strategies, settings, trainPct = 70) {
  if (!Array.isArray(strategies) || !strategies.length) {
    throw new Error("Choose at least one strategy for portfolio validation.");
  }
  const splits = strategies.map((strategy) => splitBacktest(
    candles,
    strategy.desired,
    { ...settings, ...(strategy.settings || {}) },
    trainPct
  ));
  const capital = Number(settings?.capital) || 10000;
  const trainRun = splits.length === 1
    ? splits[0].trainRun
    : combinePortfolio(splits.map((split) => split.trainRun), capital);
  const testRun = splits.length === 1
    ? splits[0].testRun
    : combinePortfolio(splits.map((split) => split.testRun), capital);
  return {
    splitIndex: splits[0].splitIndex,
    splitTime: splits[0].splitTime,
    trainRun,
    testRun,
    trainMetrics: computeMetrics(trainRun, capital),
    testMetrics: computeMetrics(testRun, capital),
  };
}

/** Expanding-window walk-forward optimization for one bounded parameter grid. */
export async function walkForwardOptimize({
  candles,
  parameterValues,
  parameterKey,
  baseParams = {},
  settings,
  metric = "calmar",
  folds = 3,
  getPositions,
  getSettings = () => settings,
  onProgress,
}) {
  if (!Array.isArray(candles) || candles.length < 24) throw new Error("Walk-forward needs at least 24 candles.");
  if (!Array.isArray(parameterValues) || parameterValues.length < 2) throw new Error("Choose at least two parameter values.");
  if (typeof getPositions !== "function") throw new Error("The strategy signal builder is unavailable.");
  if (!["sharpe", "calmar"].includes(metric)) throw new Error("Choose Sharpe or Calmar as the training score.");

  const foldCount = Math.min(5, Math.max(2, Math.floor(Number(folds) || 3)));
  const initialTrainEnd = Math.floor(candles.length * 0.5);
  const testLength = Math.floor((candles.length - initialTrainEnd) / foldCount);
  if (testLength < 2) throw new Error("There are not enough candles for the selected number of test windows.");

  const capital = Number(settings?.capital) || 10000;
  const foldRows = [];
  const oosRun = { times: [], equity: [], barReturns: [], position: [], trades: [] };
  let chainedEquity = capital;
  let testsCompleted = 0;

  for (let foldIndex = 0; foldIndex < foldCount; foldIndex += 1) {
    const trainEnd = initialTrainEnd + foldIndex * testLength;
    const testEnd = foldIndex === foldCount - 1 ? candles.length : Math.min(candles.length, trainEnd + testLength);
    const trainCandles = candles.slice(0, trainEnd);
    let winner = null;

    for (let candidateIndex = 0; candidateIndex < parameterValues.length; candidateIndex += 1) {
      await new Promise((resolve) => setTimeout(resolve, 0));
      const value = parameterValues[candidateIndex];
      const candidateParams = { ...baseParams, [parameterKey]: value };
      onProgress?.({ fold: foldIndex + 1, folds: foldCount, candidate: candidateIndex + 1, candidates: parameterValues.length });
      try {
        const desired = await getPositions(trainCandles, candidateParams);
        if (!Array.isArray(desired) || desired.length !== trainCandles.length) continue;
        const trainRun = runBacktest(trainCandles, desired, getSettings(candidateParams));
        const metrics = computeMetrics(trainRun, capital);
        const score = Number(metrics?.[metric]);
        if (Number.isFinite(score) && (!winner || score > winner.score)) {
          winner = { value, params: candidateParams, score };
        }
      } catch {
        // One invalid value should not stop the other candidates or folds.
      }
    }
    if (!winner) throw new Error(`No parameter produced a finite ${metric === "sharpe" ? "Sharpe" : "Calmar"} score in training window ${foldIndex + 1}.`);

    const prefixCandles = candles.slice(0, testEnd);
    const prefixDesired = await getPositions(prefixCandles, winner.params);
    if (!Array.isArray(prefixDesired) || prefixDesired.length !== prefixCandles.length) {
      throw new Error("The strategy did not return one signal for each candle.");
    }
    const testCandles = candles.slice(trainEnd, testEnd);
    const testDesired = prefixDesired.slice(trainEnd, testEnd);
    testDesired[0] = 0; // the signal at the training boundary is never carried into the test period
    const testRun = runBacktest(testCandles, testDesired, getSettings(winner.params));
    const testMetrics = computeMetrics(testRun, capital);
    const foldStartEquity = chainedEquity;
    let previousEquity = foldStartEquity;
    for (let i = 0; i < testRun.barReturns.length; i += 1) {
      const barReturn = Number(testRun.barReturns[i]) || 0;
      chainedEquity = Math.max(0, chainedEquity * (1 + barReturn));
      oosRun.times.push(testRun.times[i]);
      oosRun.equity.push(chainedEquity);
      oosRun.barReturns.push(previousEquity > 0 ? chainedEquity / previousEquity - 1 : 0);
      oosRun.position.push(testRun.position[i]);
      previousEquity = chainedEquity;
    }
    testRun.trades.forEach((trade) => oosRun.trades.push({
      ...trade,
      pnl: trade.pnl * (foldStartEquity / capital),
      strategy: `Fold ${foldIndex + 1}`,
    }));
    foldRows.push({
      fold: foldIndex + 1,
      trainFrom: trainCandles[0].time,
      trainTo: trainCandles[trainCandles.length - 1].time,
      testFrom: testCandles[0].time,
      testTo: testCandles[testCandles.length - 1].time,
      bestValue: winner.value,
      trainScore: winner.score,
      testMetrics,
      trades: testRun.trades.length,
    });
    testsCompleted += testRun.times.length;
  }

  if (!testsCompleted) throw new Error("No out-of-sample bars were evaluated.");
  oosRun.metrics = computeMetrics(oosRun, capital);
  return { metric, folds: foldRows, oosRun, oosMetrics: oosRun.metrics };
}

function summarizeTrades(trades) {
  const wins = trades.filter((trade) => trade.pnl > 0);
  const losses = trades.filter((trade) => trade.pnl < 0);
  const grossWin = wins.reduce((sum, trade) => sum + trade.pnl, 0);
  const grossLoss = Math.abs(losses.reduce((sum, trade) => sum + trade.pnl, 0));
  return {
    trades: trades.length,
    winRate: trades.length ? wins.length / trades.length : NaN,
    totalPnl: trades.reduce((sum, trade) => sum + trade.pnl, 0),
    profitFactor: grossLoss > 0 ? grossWin / grossLoss : grossWin > 0 ? Infinity : NaN,
  };
}

/** Attribute each trade to an approximate UTC session and both regime models at its signal candle. */
export function attributeTrades(candles, trades, {
  period = 20,
  smooth = 5,
  rangeThreshold = 0.25,
  trendThreshold = 0.4,
  hmmLookback = 20,
  hmmTrainingFraction = 0.7,
  hmmModelMode = "holdout",
  hmmResult: suppliedHmmResult,
} = {}) {
  const regimeByTime = new Map(calculateKaufmanEfficiencyRatio(candles, period, smooth).map((point) => [Number(point.time), point.value]));
  const hmmResult = suppliedHmmResult || detectHMMRegimesCached(candles, { lookback: hmmLookback, trainingFraction: hmmTrainingFraction });
  const selectedHmmModelMode = hmmModelMode === "causal200" ? "causal200" : "holdout";
  // Use the same forward-only state stream selected by the strategy.
  const attributedHmmBars = selectedHmmModelMode === "causal200"
    ? (hmmResult.causalBars || [])
    : (hmmResult.bars || []).filter((bar) => bar.phase === "holdout");
  const selectedHmmStatus = selectedHmmModelMode === "causal200"
    ? (hmmResult.causalStatus || hmmResult.status)
    : hmmResult.status;
  const selectedHmmReason = selectedHmmModelMode === "causal200"
    ? (hmmResult.causalReason || hmmResult.reason)
    : hmmResult.reason;
  const hmmByTime = new Map(attributedHmmBars.map((bar) => [Number(bar.time), bar.kind]));
  const firstAttributedHmmTime = Number(attributedHmmBars[0]?.time);
  const rangeCutoff = Math.min(1, Math.max(0, Number.isFinite(Number(rangeThreshold)) ? Number(rangeThreshold) : 0.25));
  const trendCutoff = Math.max(rangeCutoff, Math.min(1, Math.max(0, Number.isFinite(Number(trendThreshold)) ? Number(trendThreshold) : 0.4)));
  const sessions = new Map();
  const regimes = new Map();
  const hmmRegimes = new Map();
  const sessionFor = (time) => {
    const hour = new Date(time * 1000).getUTCHours();
    if (hour < 7) return "Asia · 00–07 UTC";
    if (hour < 13) return "Europe · 07–13 UTC";
    if (hour < 21) return "US · 13–21 UTC";
    return "Quiet · 21–24 UTC";
  };

  for (const trade of trades || []) {
    const session = sessionFor(Number(trade.entryTime));
    const candidateIndex = Number(trade.entryIdx);
    const signalIndex = Number.isFinite(candidateIndex) ? Math.max(0, candidateIndex - 1) : -1;
    const signalCandle = signalIndex >= 0 ? candles[signalIndex] : null;
    const signalTime = Number(signalCandle?.time);
    const efficiency = Number.isFinite(signalTime) ? regimeByTime.get(signalTime) : NaN;
    const regime = Number.isFinite(efficiency)
      ? efficiency <= rangeCutoff ? "Range" : efficiency >= trendCutoff ? "Trend" : "Mixed"
      : "Unclassified";
    let hmmRegime = "Model unavailable";
    if (selectedHmmStatus === "ready") {
      const kind = hmmByTime.get(signalTime);
      if (kind) hmmRegime = kind === "highVol" ? "High volatility" : kind === "range" ? "Range" : "Trend";
      else if (Number.isFinite(signalTime) && Number.isFinite(firstAttributedHmmTime) && signalTime < firstAttributedHmmTime) hmmRegime = "HMM warm-up (not scored)";
      else hmmRegime = "Unclassified";
    }
    if (!sessions.has(session)) sessions.set(session, []);
    if (!regimes.has(regime)) regimes.set(regime, []);
    if (!hmmRegimes.has(hmmRegime)) hmmRegimes.set(hmmRegime, []);
    sessions.get(session).push(trade);
    regimes.get(regime).push(trade);
    hmmRegimes.get(hmmRegime).push(trade);
  }
  return {
    sessions: [...sessions.entries()].map(([name, rows]) => ({ name, ...summarizeTrades(rows) })),
    regimes: [...regimes.entries()].map(([name, rows]) => ({ name, ...summarizeTrades(rows) })),
    hmmRegimes: [...hmmRegimes.entries()].map(([name, rows]) => ({ name, ...summarizeTrades(rows) })),
    hmm: {
      status: selectedHmmStatus || "unavailable",
      reason: selectedHmmReason || "",
      lookback: hmmResult.lookback || hmmLookback,
      modelMode: selectedHmmModelMode,
      trainingFraction: hmmResult.trainingFraction || hmmTrainingFraction,
      trainingBars: hmmResult.trainingBars || 0,
      validationBars: hmmResult.validationBars || 0,
      causalWarmupBars: hmmResult.causalWarmupBars || 0,
      causalReason: hmmResult.causalReason || "",
      scoreDelta: Number.isFinite(hmmResult.scoreDelta) ? hmmResult.scoreDelta : null,
    },
    period,
    smooth,
    rangeThreshold: rangeCutoff,
    trendThreshold: trendCutoff,
  };
}

function mulberry32(seed) {
  let state = seed >>> 0;
  return () => {
    state += 0x6d2b79f5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

function quantile(sorted, probability) {
  if (!sorted.length) return NaN;
  const index = Math.min(sorted.length - 1, Math.max(0, Math.floor((sorted.length - 1) * probability)));
  return sorted[index];
}

/** Bootstrap realized trade returns with replacement to show how order can affect outcomes.
 * This is a diagnostic, not a tick-level market simulation or a prediction. */
export function monteCarloTrades(trades, capital, { paths = 500, seed = 42 } = {}) {
  const returns = (trades || []).map((trade) => Number(trade?.ret)).filter((value) => Number.isFinite(value));
  const start = Number(capital);
  const count = Math.min(5000, Math.max(100, Math.floor(Number(paths) || 500)));
  if (!Number.isFinite(start) || start <= 0 || returns.length < 2) {
    return { paths: count, trades: returns.length, finalEquity: [], maxDrawdown: [], probabilityOfLoss: NaN };
  }

  const random = mulberry32(Number(seed) || 42);
  const finalEquity = new Array(count);
  const maxDrawdown = new Array(count);
  let losingPaths = 0;
  for (let path = 0; path < count; path += 1) {
    let equity = start;
    let peak = start;
    let drawdown = 0;
    for (let trade = 0; trade < returns.length; trade += 1) {
      equity = Math.max(0, equity * (1 + returns[Math.floor(random() * returns.length)]));
      peak = Math.max(peak, equity);
      if (peak > 0) drawdown = Math.min(drawdown, equity / peak - 1);
    }
    finalEquity[path] = equity;
    maxDrawdown[path] = drawdown;
    if (equity < start) losingPaths += 1;
  }
  finalEquity.sort((a, b) => a - b);
  maxDrawdown.sort((a, b) => a - b);
  return {
    paths: count,
    trades: returns.length,
    finalEquity,
    maxDrawdown,
    probabilityOfLoss: losingPaths / count,
    p05Equity: quantile(finalEquity, 0.05),
    medianEquity: quantile(finalEquity, 0.5),
    p95Equity: quantile(finalEquity, 0.95),
    p05Drawdown: quantile(maxDrawdown, 0.05),
  };
}
